import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Placement as StatuspageNavigationSpotlightPlacement } from '@post-office/placements/statuspage-navigation-spotlight-placement/frontend';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import UFOLabel from '@jira/platform__app-framework-ufo-label/src/index.tsx';
import { MENU_ID_OPERATIONS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Nav4DragAndDropMenuItemProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../common/constants.tsx';
import type { Nav4Operations$key } from './__generated__/Nav4Operations.graphql';
import { OperationsMenuDefault } from './OperationsMenuDefault.tsx';
import { OperationsMenuForLanding } from './OperationsMenuForLanding.tsx';
import { AlertsPopoutStoreContainer } from './operations-expandable-menu-item/action-buttons/useAlertsPopoutStore.tsx';

export type Nav4OperationsProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRef: Nav4Operations$key;
};

export const Nav4Operations = (props: Nav4OperationsProps): React.JSX.Element => {
	const { isExpanded, queryRef, ...dndProps } = props;
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4Operations$key>(
		graphql`
			fragment Nav4Operations on Query {
				...OperationsMenuForLanding @dangerously_unaliased_fixme @include(if: $isOperationsExpanded)
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);
	const { isInitialSelectedPath } = useSidebarNav4();
	const isExpandedValue = fg('navx-global-side-nav')
		? !!props.isExpandOrFlyoutOpen && props.isExpanded
		: props.isExpanded;

	const contents = isInitialSelectedPath(MENU_ID_OPERATIONS) ? (
		<OperationsMenuForLanding isExpanded={isExpandedValue} queryRef={data} {...dndProps} />
	) : (
		<OperationsMenuDefault isExpanded={isExpandedValue} {...dndProps} />
	);

	return (
		<UFOLabel name="nav4-sidebar-operations">
			<JSErrorBoundary
				fallback="flag"
				id="operations-section"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
			>
				{fg('communications_status_page_manage_spotlight') ? (
					<AlertsPopoutStoreContainer isGlobal>
						<StatuspageNavigationSpotlightPlacement />
						{contents}
					</AlertsPopoutStoreContainer>
				) : (
					<AlertsPopoutStoreContainer isGlobal>{contents}</AlertsPopoutStoreContainer>
				)}
			</JSErrorBoundary>
		</UFOLabel>
	);
};
